// ════════════════════════════════════════════════════════════════
// FloorCost — per-user/per-store localStorage namespacing (SAAS S1-5)
// ────────────────────────────────────────────────────────────────
// A shared counter device is used by multiple operators. Per-user work
// and queue keys must not cross-contaminate between operators, so at
// boot (once the signed-in uid is known) we repoint the shared STORAGE
// map at `<base>__<uid>__<store>` keys. Every consumer — import-time
// aliases (tracker/queue/archive/templates) and call-time
// `STORAGE.x` reads alike — transparently picks up the namespaced key
// because they all resolve through this one object.
//
// Identity/credential/preference keys (auth, authPending, passkey,
// theme, roundUp) are deliberately NOT namespaced — see the
// classification-pin test in tests/ns-storage.test.js.
//
// Fail-safe: a falsy uid/store yields the base keys (today's behaviour),
// so a signed-out or pre-identity read is never namespaced.
// ════════════════════════════════════════════════════════════════

import { STORAGE } from '#lib/config.js';

// Snapshot the base (un-namespaced) key literals at module-eval, BEFORE any
// runtime mutation of the shared STORAGE object — so every namespaced key is
// always derived from the pristine base, never from an already-mutated value
// (which would double-namespace on a re-entrant boot).
const STORAGE_BASE = { ...STORAGE };

// Device-global one-time-migration flag (CRIT-1). The legacy → namespaced
// rename runs exactly ONCE per device — the first post-deploy load, for
// whichever operator does it. A later operator on a shared counter therefore
// inherits NO legacy work (they start fresh in their own namespace) instead of
// silently absorbing the previous operator's in-progress data.
const NS_MIGRATED_FLAG = 'fc_ns_migrated_v1';

/**
 * The per-user/per-store work + queue keys (property names on STORAGE).
 * A new STORAGE entry that holds per-operator state MUST be added here or it
 * escapes isolation — the classification-pin test guards against that.
 */
export const NAMESPACED_KEYS = [
  'form',
  'tracker',
  'archive',
  'templates',
  'qjc',
  'blinds',
  'toolkit',
  'toolkitCommunications',
  'toolkitDescription',
  'toolkitScopePresets',
  'measure',
  'decisionLog',
  'trackerViews',
  'trackerSort',
  'editBindings',
  'syncQueue',
  'syncQueueDead',
];

/**
 * Sanitised `__<uid>__<store>` suffix, or '' when either part is falsy — the
 * fail-safe that keeps the base keys (today's behaviour) when there's no
 * identity yet.
 * @param {string} uid
 * @param {string} store
 * @returns {string}
 */
export function nsSuffix(uid, store) {
  const u = String(uid || '').replace(/[^A-Za-z0-9-]/g, '');
  const s = String(store || '').replace(/[^A-Za-z0-9-]/g, '');
  return u && s ? `__${u}__${s}` : '';
}

/**
 * A base storage key namespaced to (uid, store); the base key itself when the
 * suffix is empty.
 * @param {string} base
 * @param {string} uid
 * @param {string} store
 * @returns {string}
 */
export function nsKey(base, uid, store) {
  return base + nsSuffix(uid, store);
}

/**
 * One-time copy of each legacy base key's value into its (uid, store)
 * namespace. Verified-copy-before-delete (no data loss on a quota/private
 * failure) and never clobbers an already-populated namespaced slot (no
 * cross-operator overwrite).
 *
 * @returns {boolean} true iff every key was migrated or had nothing to
 *   migrate — so the caller only sets the one-time device flag on FULL
 *   success and a partial (quota) failure retries on the next load rather
 *   than orphaning the un-copied base-key value forever.
 * @param {Storage} storage
 * @param {string} uid
 * @param {string} store
 */
export function migrateNamespaceKeys(storage, uid, store) {
  if (!nsSuffix(uid, store)) return false;
  let allOk = true;
  for (const name of NAMESPACED_KEYS) {
    const base = STORAGE_BASE[name];
    const target = nsKey(base, uid, store);
    if (target === base) continue;
    let legacy;
    try { legacy = storage.getItem(base); } catch { allOk = false; continue; }
    if (legacy == null) continue; // nothing to migrate for this key
    let existing;
    try { existing = storage.getItem(target); } catch { allOk = false; continue; }
    if (existing != null) continue; // already migrated — never clobber the operator's slot
    try {
      storage.setItem(target, legacy);
      if (storage.getItem(target) === legacy) storage.removeItem(base); // verified copy before delete
      else allOk = false; // copy didn't stick
    } catch {
      allOk = false; // quota/private — leave legacy intact, no data loss
    }
  }
  return allOk;
}

// The `<uid>__<store>` suffix the STORAGE map is currently pointed at, or null
// when unhydrated. NOT a once-per-life boolean: a different identity applied
// in the same module graph (an in-page user switch — the fast-swap on a shared
// counter after an in-page setSession(null)) must be DETECTED, because
// repointing STORAGE alone can't clear the previous operator's in-memory
// signals; the caller reloads to re-init cleanly.
let _appliedIdentity = null;

/**
 * Repoint the shared STORAGE map at the (uid, store)-namespaced keys and run
 * the one-time legacy migration (device-global flag). Fail-safe on a falsy
 * uid/store (leaves base keys).
 * @param {string} uid
 * @param {string} store
 * @param {{ storage?: Storage, map?: Record<string, string> }} [deps]
 * @returns {'noop'|'applied'|'unchanged'|'switched'} 'switched' means a
 *   DIFFERENT identity is signing in without a fresh module graph — the caller
 *   MUST reload (STORAGE is deliberately left untouched so the reload re-applies
 *   from scratch). 'unchanged' = same operator (re-paint / token refresh).
 */
export function applyStorageNamespace(uid, store, { storage = localStorage, map = STORAGE } = {}) {
  const suffix = nsSuffix(uid, store);
  if (!suffix) return 'noop'; // fail-safe: no identity → base keys
  if (_appliedIdentity === suffix) return 'unchanged'; // same operator
  if (_appliedIdentity !== null) return 'switched'; // different operator, same graph → caller reloads
  try {
    if (storage.getItem(NS_MIGRATED_FLAG) == null) {
      const ok = migrateNamespaceKeys(storage, uid, store);
      // Only mark the device migrated on FULL success — a partial (quota)
      // failure retries next load instead of orphaning un-copied state.
      if (ok) {
        try { storage.setItem(NS_MIGRATED_FLAG, '1'); } catch { /* flag best-effort */ }
      }
    }
  } catch {
    /* non-fatal — the mapping below still applies */
  }
  for (const name of NAMESPACED_KEYS) map[name] = nsKey(STORAGE_BASE[name], uid, store);
  _appliedIdentity = suffix;
  return 'applied';
}

/** Test-only: restore the STORAGE singleton to base keys + re-arm the applier. */
export function __resetNamespaceForTests() {
  _appliedIdentity = null;
  for (const name of NAMESPACED_KEYS) STORAGE[name] = STORAGE_BASE[name];
}
